import React, { useState, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Checkbox, Modal, Typography, Button, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd/es/upload/interface'
import { CourseContext } from '../Course/CourseContext'

import { FlexRow } from '../FlexboxReusables/FlexItems'
import { FileContext } from '../Course/FileContext'
import { createDefaultCourse } from '../Editor/misc'
import { AuthContext } from '../Auth/AuthContext'

enum CheckboxType {
  NEW = 'new',
  PDF = 'pdf'
}

type NewCourseSettingsModalProps = {
  open: boolean
  setShowModal: (show: boolean) => void
  onCancel: () => void
}

const NewCourseSettingsModal: React.FC<NewCourseSettingsModalProps> = (props) => {
  const { open, setShowModal, onCancel } = props

  const intl = useIntl()
  const navigate = useNavigate()
  const { setSelectedFile } = useContext(FileContext)
  const { createCourse } = useContext(CourseContext)
  const { authUser } = useContext(AuthContext)

  const [ useEmptyCourse, setUseEmptyCourse ] = useState(true)
  const [ usePdfTemplate, setUsePdfTemplate ] = useState(false)

  const [ fileList, setFileList ] = useState<UploadFile[]>([])

  const handleCheckboxChange = (checked: boolean, type: string) => {
    if (type === CheckboxType.NEW && checked) {
      setUseEmptyCourse(true)
      setUsePdfTemplate(false)
    } else if (CheckboxType.PDF && checked) {
      setUsePdfTemplate(true)
      setUseEmptyCourse(false)
    }
  }

  const createNewCourse = async () => {
    if (useEmptyCourse) {
      const defaultCourse = createDefaultCourse(intl, authUser.id)
      const newCreatedCourse = await createCourse(defaultCourse)

      navigate(`/course/${newCreatedCourse.id}`)
    }
    
    if (usePdfTemplate) {
      navigate('/course/fromPdf', {
        state: 
        {
          fromPdf: true,
          pdfFile: fileList[0]
        }
      })
    }

    setShowModal(false)
  }

  const uploadProps: UploadProps = {
    accept: 'application/pdf',
    onRemove: (file) => {
      const index = fileList.indexOf(file)
      const newFileList = fileList.slice()
      newFileList.splice(index, 1)
      setFileList(newFileList)
    },
    beforeUpload: (file) => {
      setFileList([ ...fileList, file ])

      return false
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        const file = info.file.originFileObj as File

        setSelectedFile(file)
      }
    },
    fileList
  }

  return (
    <Modal
      title={<FormattedMessage id='navigation.courses.createNewCourse' />}
      open={open}
      centered
      onCancel={() => onCancel()}
      footer={[
        <Button key="cancel" onClick={() => onCancel()}>
          <FormattedMessage id='generic.cancel' />
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={() => createNewCourse()}
        >
          <FormattedMessage id='generic.create' />
        </Button>
      ]}
    >
      <FlexRow justifyContent='flex-start' margin='1.2em 0'>
        <Typography.Text>
          <FormattedMessage id='course.newCourse.settings.creationOptions' />
        </Typography.Text>
      </FlexRow>
      <FlexRow justifyContent='flex-start' margin='1em 0'>
        <Checkbox
          checked={useEmptyCourse}
          onChange={(e) => handleCheckboxChange(e.target.checked, CheckboxType.NEW)}
        >
          <FormattedMessage id='course.newCourse.settings.creationOptions.emptyCourse' />
        </Checkbox>
      </FlexRow>
      <FlexRow justifyContent='flex-start' margin='1em 0 2em 0'>
        <Checkbox
          checked={usePdfTemplate}
          onChange={(e) => handleCheckboxChange(e.target.checked, CheckboxType.PDF)}
        >
          <FormattedMessage id='course.newCourse.settings.creationOptions.createFromPdfTemplate' />
        </Checkbox>
      </FlexRow>
      {usePdfTemplate && (
        <>
          <FlexRow justifyContent='center'>
            <WarningTextHeader>
              <FormattedMessage id='course.newCourse.settings.note' />
            </WarningTextHeader>
          </FlexRow>
          <FlexRow justifyContent='center' margin='1em 0'>
            <WarningTextListItems>
              <ul>
                <li>
                  <FormattedMessage id='course.newCourse.settings.note.listItem1' />
                </li>
                <li>
                  <FormattedMessage id='course.newCourse.settings.note.listItem2' />
                </li>
                <li>
                  <FormattedMessage id='course.newCourse.settings.note.listItem3' />
                </li>
              </ul>
            </WarningTextListItems>

          </FlexRow>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined style={{ marginRight: '0.8em' }}/> }>
              <FormattedMessage id='course.newCourse.settings.selectPdfFile' />
            </Button>
          </Upload>
        </>
      )}
    </Modal>
  )
}

export default NewCourseSettingsModal

const WarningTextHeader = styled(Typography.Text)`
  font-size: 1.1em;
  font-weight: 600;
`

const WarningTextListItems = styled(Typography.Text)`
  font-size: 0.9em;
  font-style: italic;
  font-weight: 500;
`